import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Close } from '@mui/icons-material';
import { Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
  GridValueOptionsParams,
} from '@mui/x-data-grid-premium';
import { sort } from 'fast-sort';
import {
  EstateTaskCustom,
  EstateTaskCustom_FieldsFragment,
  EstateTaskCustom_GetEstateTaskCustomByIdDocument,
  EstateTaskCustom_GetEstateTaskCustomByIdQuery,
  EstateType,
  useEstateTaskCustom_GetAllEstateTaskCustomsByPortalIdQuery,
  useEstateTaskCustom_UpdateEstateTaskCustomMutation,
} from 'queries';

import Spinner from 'common/spinner';
import { EstateTaskGroupLabel, getEstateTaskGroupSelectOptions } from 'utils/estate';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  h5: {
    paddingTop: '5px',
  },
  h6: {
    paddingTop: '4px',
    marginBottom: '5px',
    fontSize: '1em',
  },
}));

export default function ManageCustomTasksForm() {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const { data: estateTaskCustomsData, loading: estateTaskCustomsLoading } =
    useEstateTaskCustom_GetAllEstateTaskCustomsByPortalIdQuery({
      variables: { input: { portalId } },
      onError: (e) => {
        showAlert({
          message: 'Error getting custom estate tasks by portal ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [updateEstateTaskCustom] = useEstateTaskCustom_UpdateEstateTaskCustomMutation({
    update: (store, { data: updatedEstateTaskCustomData }) => {
      const updatedEstateTaskCustom = updatedEstateTaskCustomData?.data;
      if (updatedEstateTaskCustom) {
        store.writeQuery<EstateTaskCustom_GetEstateTaskCustomByIdQuery>({
          query: EstateTaskCustom_GetEstateTaskCustomByIdDocument,
          data: { data: updatedEstateTaskCustom },
          variables: { input: { id: updatedEstateTaskCustom.id } },
        });
      }
    },
    onError: (e) => {
      showAlert({
        message: `Error updating custom estate task: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel) => {
      const newEstateTaskCustom = newRow as EstateTaskCustom;
      // choose the first available group in newly selected estate type
      const groupSelectOptions = getEstateTaskGroupSelectOptions(newEstateTaskCustom.estateType);
      let newGroup = groupSelectOptions.find(
        (group) => group.value === newEstateTaskCustom.group,
      )?.value;
      if (!newGroup) {
        newGroup = groupSelectOptions[0].value;
      }
      updateEstateTaskCustom({
        variables: {
          input: {
            id: newEstateTaskCustom.id,
            estateType: newEstateTaskCustom.estateType,
            group: newGroup,
            label: newEstateTaskCustom.label,
            labelHint: newEstateTaskCustom.labelHint,
            isActive: newEstateTaskCustom.isActive,
            sortOrder: newEstateTaskCustom.sortOrder,
          },
        },
      });
      return newEstateTaskCustom;
    },
    [updateEstateTaskCustom],
  );

  const handleProcessRowUpdateError = useCallback((e: Error) => {
    showAlert({
      message: `Error updating custom estate task: row update error: ${e.message}`,
      severity: NotificationSeverity.ERROR,
    });
  }, []);

  if (!estateTaskCustomsData && estateTaskCustomsLoading) {
    return <Spinner />;
  }

  const estateTaskCustoms = estateTaskCustomsData!.data;

  const columns: GridColDef[] = [
    {
      field: 'sortOrder',
      headerName: 'Sort Order',
      type: 'number',
      editable: true,
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as EstateTaskCustom;
        return <Typography>{row.sortOrder}</Typography>;
      },
    },
    {
      field: 'estateType',
      headerName: 'Estate Type',
      type: 'singleSelect',
      valueOptions: Object.values(EstateType),
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as EstateTaskCustom;
        return (
          <>
            <Tooltip title={row.estateType} placement="top">
              <Typography>{row.estateType}</Typography>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: 'group',
      headerName: 'Group',
      type: 'singleSelect',
      valueOptions: (params: GridValueOptionsParams) => {
        const estateTaskCustom = params.row as EstateTaskCustom;
        return getEstateTaskGroupSelectOptions(estateTaskCustom.estateType);
      },
      editable: true,
      disableColumnMenu: true,
      flex: 5,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as EstateTaskCustom;
        return (
          <>
            <Tooltip title={EstateTaskGroupLabel[row.group]} placement="top">
              <Typography>{EstateTaskGroupLabel[row.group]}</Typography>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: 'label',
      headerName: 'Task',
      editable: true,
      disableColumnMenu: true,
      flex: 4,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as EstateTaskCustom;
        return (
          <>
            <Tooltip title={row.label} placement="top">
              <Typography>{row.label}</Typography>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: 'labelHint',
      headerName: 'Description',
      editable: true,
      disableColumnMenu: true,
      flex: 4,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as EstateTaskCustom;
        return (
          <>
            <Tooltip title={row.labelHint} placement="top">
              <Typography>{row.labelHint}</Typography>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: 'isActive',
      headerName: 'Active',
      type: 'boolean',
      editable: true,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? <Check /> : <Close />;
      },
    },
  ];

  const rows = sort(estateTaskCustoms || []).by([
    {
      asc: (estateTaskCustom: EstateTaskCustom_FieldsFragment) => estateTaskCustom.sortOrder,
    },
    {
      asc: (estateTaskCustom: EstateTaskCustom_FieldsFragment) =>
        new Date(estateTaskCustom.createdAt),
    },
  ]);

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        Manage Custom Tasks
      </Typography>
      <Typography variant="h6" className={classes.h6}>
        Double-click to edit a field
      </Typography>
      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          className={classes.root}
          columns={columns}
          rows={rows}
          disableColumnReorder
          disableRowSelectionOnClick
          autoHeight={true}
          rowHeight={65}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          hideFooter
        />
      </div>
    </>
  );
}
